import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type { NotificationItem, UUID } from "@/lib/types";

const POLL_INTERVAL_MS = 30_000;

export function useNotifications() {
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: queryKeys.notifications,
    queryFn: () => api.listNotifications(15),
    refetchInterval: POLL_INTERVAL_MS,
  });

  const unreadCount = useQuery({
    queryKey: queryKeys.notificationsUnread,
    queryFn: () => api.getNotificationsUnreadCount(),
    refetchInterval: POLL_INTERVAL_MS,
  });

  const markRead = useMutation<{ marked: number }, Error, UUID>({
    mutationFn: (id) => api.markNotificationRead(id),
    onMutate: async (id) => {
      // Optimistically mark the item as read in the cached list.
      await qc.cancelQueries({ queryKey: queryKeys.notifications });
      const prev = qc.getQueryData<NotificationItem[]>(queryKeys.notifications) ?? [];
      const nowIso = new Date().toISOString();
      qc.setQueryData<NotificationItem[]>(queryKeys.notifications, (curr) =>
        (curr ?? []).map((n) => (n.id === id && !n.read_at ? { ...n, read_at: nowIso } : n)),
      );
      // Decrement unread count locally for snappy badge.
      const prevCount = qc.getQueryData<{ count: number }>(queryKeys.notificationsUnread);
      if (prevCount && prevCount.count > 0) {
        qc.setQueryData(queryKeys.notificationsUnread, { count: prevCount.count - 1 });
      }
      return { prev, prevCount };
    },
    onError: (_err, _id, ctx) => {
      const c = ctx as { prev?: NotificationItem[]; prevCount?: { count: number } } | undefined;
      if (c?.prev) qc.setQueryData(queryKeys.notifications, c.prev);
      if (c?.prevCount) qc.setQueryData(queryKeys.notificationsUnread, c.prevCount);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.notifications });
      void qc.invalidateQueries({ queryKey: queryKeys.notificationsUnread });
    },
  });

  const markAllRead = useMutation<{ marked: number }, Error, void>({
    mutationFn: () => api.markAllNotificationsRead(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: queryKeys.notifications });
      const prev = qc.getQueryData<NotificationItem[]>(queryKeys.notifications) ?? [];
      const nowIso = new Date().toISOString();
      qc.setQueryData<NotificationItem[]>(queryKeys.notifications, (curr) =>
        (curr ?? []).map((n) => (n.read_at ? n : { ...n, read_at: nowIso })),
      );
      qc.setQueryData(queryKeys.notificationsUnread, { count: 0 });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      const c = ctx as { prev?: NotificationItem[] } | undefined;
      if (c?.prev) qc.setQueryData(queryKeys.notifications, c.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.notifications });
      void qc.invalidateQueries({ queryKey: queryKeys.notificationsUnread });
    },
  });

  return {
    items: list.data ?? [],
    isLoading: list.isLoading,
    unreadCount: unreadCount.data?.count ?? 0,
    markRead,
    markAllRead,
  };
}
